/**
 * WebSocketProvider — manages the WS connection and event dispatch for React.
 *
 * Connects to the server's /ws endpoint with auth cookie, subscribes to
 * channels (queue, inbox), and provides a hook for components to listen
 * to real-time events.
 */

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useCallback,
  useState,
  type ReactNode,
} from 'react';
import { useAuth, refreshToken } from './AuthProvider';
import { dropContextCache as dropPickerContextCache } from '../hooks/useMyNumbers';
import { getLastActivityAt } from '../lib/idle';
import { redirectToLogin } from '../lib/login-redirect';

// ── Types ────────────────────────────────────────────────────────────────

type EventHandler = (data: unknown) => void;

interface WebSocketContextValue {
  /** True when connected */
  connected: boolean;
  /** Subscribe to an event type. Returns unsubscribe function. */
  subscribe: (event: string, handler: EventHandler) => () => void;
  /** Send a JSON message to the server */
  send: (type: string, payload?: Record<string, unknown>) => void;
}

const WebSocketContext = createContext<WebSocketContextValue | null>(null);

// ── Provider ─────────────────────────────────────────────────────────────

/** How often to send a presence heartbeat while the WS is open. Must be
 *  well under the server sweep's 120s stale threshold. */
const HEARTBEAT_INTERVAL_MS = 30_000;

/** Reconnect backoff bounds. Starts at MIN, doubles per failure, caps at
 *  MAX, and resets to MIN on a successful open. The previous fixed 3s
 *  spammed errors during rolling deploys (new pod takes 5–30s to become
 *  Ready) without giving up early; an exponential schedule both quiets
 *  the noise and recovers fast on transient blips. A clean server-side
 *  shutdown (close code 1001) fast-paths past the backoff entirely. */
const RECONNECT_DELAY_MIN_MS = 1_000;
const RECONNECT_DELAY_MAX_MS = 30_000;

/** WebSocket close code 1001 = "going away" — the server sent a proper
 *  close frame, typically because it's shutting down for a deploy. */
const WS_CLOSE_GOING_AWAY = 1001;

/** App close code 4001 = JWT expired. The server checks tokenExp on every
 *  inbound message and closes the socket once the access token has lapsed.
 *  Reconnecting with the same expired cookie just produces a 401 on the
 *  next upgrade, so we have to rotate the cookie before the retry. */
const WS_CLOSE_TOKEN_EXPIRED = 4001;

/** Window event name for version-drift detection. App.tsx listens and
 *  surfaces a "new version, refresh" toast. Single fire per session — once
 *  we've nudged the user, they own deciding when to reload (active call,
 *  draft message, etc.). */
const VERSION_DRIFT_EVENT = 'apex:version-drift';

export function WebSocketProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, refreshUser, logout } = useAuth();
  const wsRef = useRef<WebSocket | null>(null);
  const listenersRef = useRef<Map<string, Set<EventHandler>>>(new Map());
  const [connected, setConnected] = useState(false);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const heartbeatTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const reconnectDelayRef = useRef<number>(RECONNECT_DELAY_MIN_MS);
  // Build SHA the server reported on the first connect this session.
  // Subsequent reconnects compare against this to detect rolling-deploy
  // version drift; null until the first system.build_info arrives.
  const firstBuildShaRef = useRef<string | null>(null);
  const versionDriftDispatchedRef = useRef<boolean>(false);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const ws = new WebSocket(`${protocol}//${window.location.host}/ws`);

    ws.onopen = () => {
      setConnected(true);
      // Reset backoff on a successful open — the next disconnect starts
      // from MIN regardless of how long the previous outage lasted.
      reconnectDelayRef.current = RECONNECT_DELAY_MIN_MS;
      // Subscribe to default channels (server expects 'action', not 'type').
      // `kpi` carries rep presence/status changes used by live supervisor
      // views; subscribing here means every authenticated tab gets the
      // events and individual views just attach listeners.
      ws.send(JSON.stringify({ action: 'subscribe', channel: 'queue' }));
      ws.send(JSON.stringify({ action: 'subscribe', channel: 'inbox' }));
      ws.send(JSON.stringify({ action: 'subscribe', channel: 'kpi' }));
      ws.send(JSON.stringify({ action: 'subscribe', channel: 'phone-config' }));
      // `bulk_operations` carries progress for the caller's own contacts bulk
      // actions (#1586). Subscribed here rather than in the Contacts page so a
      // run started before navigating away still reports when it lands — the
      // whole point is that the work doesn't depend on a tab staying put. The
      // server only pushes these to the user who started the operation.
      ws.send(JSON.stringify({ action: 'subscribe', channel: 'bulk_operations' }));

      // Presence heartbeat. The server updates this tab's presence entry's
      // last_heartbeat_at; the cron sweeper drops entries stale beyond 120s.
      // We send one immediately so a just-opened tab doesn't wait a full
      // interval before registering as alive.
      //
      // Also rides the input-activity timestamp (epoch-ms UTC) so the
      // server-side idle-sweep can fire 15m clock-out / 60m force-logout
      // thresholds. Read fresh on every send — the value updates as the
      // user moves the mouse / types between heartbeats.
      const buildHeartbeat = () =>
        JSON.stringify({ action: 'heartbeat', last_activity_at: getLastActivityAt() });
      ws.send(buildHeartbeat());
      if (heartbeatTimer.current) clearInterval(heartbeatTimer.current);
      heartbeatTimer.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(buildHeartbeat());
        }
      }, HEARTBEAT_INTERVAL_MS);
    };

    ws.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        const eventType = msg.type || msg.event;
        if (!eventType) return;

        // Server hello frame: first occurrence pins the build SHA for this
        // session; later occurrences with a different SHA mean a rolling
        // deploy happened while this tab was open and the loaded SPA
        // bundle no longer matches the running pod. Fire the drift event
        // exactly once — the listener decides what to do (we show a
        // toast; nothing auto-reloads, an active softphone session must
        // not be killed without user consent). "dev" SHAs (local builds
        // without --build-arg) are treated as no-op on either side.
        if (eventType === 'system.build_info' && typeof msg.sha === 'string') {
          if (firstBuildShaRef.current === null) {
            firstBuildShaRef.current = msg.sha;
          } else if (
            !versionDriftDispatchedRef.current &&
            msg.sha !== firstBuildShaRef.current &&
            msg.sha !== 'dev' &&
            firstBuildShaRef.current !== 'dev'
          ) {
            versionDriftDispatchedRef.current = true;
            window.dispatchEvent(
              new CustomEvent(VERSION_DRIFT_EVENT, {
                detail: { from: firstBuildShaRef.current, to: msg.sha },
              }),
            );
          }
          return;
        }

        const handlers = listenersRef.current.get(eventType);
        if (handlers) {
          handlers.forEach((fn) => fn(msg));
        }

        // Also dispatch to wildcard listeners
        const wildcard = listenersRef.current.get('*');
        if (wildcard) {
          wildcard.forEach((fn) => fn(msg));
        }
      } catch {
        // Ignore non-JSON messages
      }
    };

    ws.onclose = (evt) => {
      setConnected(false);
      wsRef.current = null;
      if (heartbeatTimer.current) {
        clearInterval(heartbeatTimer.current);
        heartbeatTimer.current = null;
      }
      // Server-initiated graceful shutdown (rolling deploy) sends close
      // code 1001 — the new pod is already Ready behind the Service so
      // we can reconnect immediately. Other close paths (1006 abnormal,
      // network blip, server crash) use exponential backoff so we don't
      // spam connection attempts while a pod is still starting.
      if (evt.code === WS_CLOSE_GOING_AWAY) {
        reconnectDelayRef.current = RECONNECT_DELAY_MIN_MS;
        reconnectTimer.current = setTimeout(connect, 0);
        return;
      }
      // JWT expired mid-session. Rotate the cookie via /auth/refresh
      // before reconnecting; otherwise the upgrade handshake will 401
      // on the same stale cookie and we'd loop in exponential backoff
      // until the user reloaded. Mirrors what apiFetch already does for
      // REST 401s. If refresh fails the refresh token has lapsed too —
      // only login can recover, same threshold apiFetch enforces.
      if (evt.code === WS_CLOSE_TOKEN_EXPIRED) {
        void (async () => {
          const ok = await refreshToken();
          if (!ok) {
            redirectToLogin(window.location.pathname);
            return;
          }
          reconnectDelayRef.current = RECONNECT_DELAY_MIN_MS;
          reconnectTimer.current = setTimeout(connect, 0);
        })();
        return;
      }
      reconnectTimer.current = setTimeout(connect, reconnectDelayRef.current);
      reconnectDelayRef.current = Math.min(reconnectDelayRef.current * 2, RECONNECT_DELAY_MAX_MS);
    };

    ws.onerror = () => {
      ws.close();
    };

    wsRef.current = ws;
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    connect();
    return () => {
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (heartbeatTimer.current) {
        clearInterval(heartbeatTimer.current);
        heartbeatTimer.current = null;
      }
      wsRef.current?.close();
    };
  }, [isAuthenticated, connect]);

  const subscribe = useCallback((event: string, handler: EventHandler) => {
    if (!listenersRef.current.has(event)) {
      listenersRef.current.set(event, new Set());
    }
    listenersRef.current.get(event)!.add(handler);

    return () => {
      listenersRef.current.get(event)?.delete(handler);
    };
  }, []);

  // Server-pushed `auth.refresh_required` → rotate the JWT and reload the
  // user record. Fired when an admin changes this user's role (see
  // PUT /admin/reps/:id) so the UI reflects the new permissions without
  // a logout/login round-trip.
  useEffect(() => {
    return subscribe('auth.refresh_required', () => {
      void refreshUser();
    });
  }, [subscribe, refreshUser]);

  // Server-pushed `auth.force_logout` → cron idle-sweep determined this
  // user has been inactive past the 60-min logout threshold. Run the
  // standard logout flow so the browser navigates to the Keycloak end-
  // session endpoint and lands on the sign-in page.
  useEffect(() => {
    return subscribe('auth.force_logout', () => {
      void logout();
    });
  }, [subscribe, logout]);

  // Phone-config admin writes (phone tagged to a brand/agency, picker
  // tags revoked, etc.) drop the cached picker-context so the next
  // useMyNumbers consumer fetches the fresh association map. Cheap —
  // these events fire only on rare admin actions.
  useEffect(() => {
    return subscribe('phone-config-changed', () => {
      dropPickerContextCache();
    });
  }, [subscribe]);

  const send = useCallback((type: string, payload?: Record<string, unknown>) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type, ...payload }));
    }
  }, []);

  return (
    <WebSocketContext.Provider value={{ connected, subscribe, send }}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocket(): WebSocketContextValue {
  const ctx = useContext(WebSocketContext);
  if (!ctx) throw new Error('useWebSocket must be used inside WebSocketProvider');
  return ctx;
}
